import equal from "fast-deep-equal/es6";
import { action, observable, runInAction } from "mobx";

import { OnlineDevicesClient } from "@formant/core/client/OnlineDevicesClient";
import {
    IPromiseIntervalHandle,
    setPromiseInterval,
} from "@formant/core/common/setPromiseInterval";
import { duration } from "@formant/core/model/duration";
import { Uuid } from "@formant/core/model/Uuid";

import { IAuthStore } from "./IAuthStore";
import { IOnlineDevicesStore } from "./IOnlineDevicesStore";
import { StoreCache } from "./StoreCache";

const onlineDeviceTTL = 5 * duration.second;
const onlineDevicePollingInterval = 5 * duration.second;

export class OnlineDevicesStore implements IOnlineDevicesStore {
    @observable
    private onlineDevices: Set<Uuid> = new Set();

    private cache = new StoreCache<undefined, Set<Uuid>>({
        staleIntervalMs: onlineDeviceTTL,
        isEqual: equal,
    });

    private shutdownRefresh: IPromiseIntervalHandle | null = null;

    constructor(
        private onlineDevicesClient: OnlineDevicesClient,
        private authStore: IAuthStore,
    ) {
        this.cache.watch({ onStart: this.resume, onEnd: this.suspend });
    }

    @action
    private updateOnlineDevices(devices: Set<Uuid>) {
        this.onlineDevices = devices;
    }

    online(): Set<Uuid> | undefined {
        // Always go through cache.get() to keep MobX observation of
        // cache.entries alive — this drives the 5-second polling via
        // onBecomeObserved → resume(). Without this call, polling dies.
        this.cache.get(undefined, async () => this.fetch());
        return this.onlineDevices.size > 0 ? this.onlineDevices : undefined;
    }

    isOnline(id: Uuid): boolean | undefined {
        const online = this.online();
        if (!online) {
            return undefined;
        }
        return online.has(id);
    }

    async fetch(): Promise<Set<Uuid>> {
        try {
            const devices = new Set(
                await this.onlineDevicesClient.online(
                    await this.authStore.token(),
                ),
            );
            runInAction(() => {
                this.updateOnlineDevices(devices);
            });
            return devices;
        } catch (error) {
            console.error("Failed to fetch online devices:", error);
            return this.onlineDevices;
        }
    }

    private resume = () => {
        if (!this.shutdownRefresh) {
            this.shutdownRefresh = setPromiseInterval({
                func: () => this.cache.refresh(undefined, () => this.fetch()),
                delay: onlineDevicePollingInterval,
            });
        }
    };

    private suspend = () => {
        if (this.shutdownRefresh) {
            void this.shutdownRefresh.stop();
            this.shutdownRefresh = null;
        }
    };
}
